package com.merkost.handpass.admin.components

import androidx.compose.runtime.Composable
import com.merkost.handpass.sharedweb.components.WebIcon
import com.merkost.handpass.sharedweb.components.Icon
import com.merkost.handpass.sharedweb.theme.AccentRing
import com.merkost.handpass.sharedweb.theme.Border
import com.merkost.handpass.sharedweb.theme.BorderStrong
import com.merkost.handpass.sharedweb.theme.Danger
import com.merkost.handpass.sharedweb.theme.DangerSoft
import com.merkost.handpass.sharedweb.theme.OnSurface
import com.merkost.handpass.sharedweb.theme.OnSurfaceFaint
import com.merkost.handpass.sharedweb.theme.OnSurfaceMuted
import com.merkost.handpass.sharedweb.theme.RadiusControl
import com.merkost.handpass.sharedweb.theme.Success
import com.merkost.handpass.sharedweb.theme.SuccessSoft
import com.merkost.handpass.sharedweb.theme.Surface
import com.merkost.handpass.sharedweb.theme.TouchTarget
import com.merkost.handpass.sharedweb.theme.WebType
import com.merkost.handpass.sharedweb.theme.transitionSurfaceAction
import com.merkost.handpass.sharedweb.theme.transitionReveal
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.translateY
import com.varabyte.kobweb.compose.ui.modifiers.transition
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.extendedBy
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.attributes.AttrsScope
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLButtonElement

enum class AdminIconTone { Neutral, Ok, Danger }

val AdminRowActionsStyle = CssStyle.base {
    Modifier
        .display(DisplayStyle.Flex)
        .gap(6.px)
        .justifyContent(JustifyContent.FlexEnd)
        .styleModifier { property("transform", "translateX(6px)") }
        .transitionReveal()
        .styleModifier { property("opacity", "0") }
}

@Composable
fun AdminRowActions(content: @Composable () -> Unit) {
    Div(attrs = AdminRowActionsStyle.toModifier().classNames("admin-row-actions").toAttrs()) {
        content()
    }
}

val AdminIconButtonBaseStyle = CssStyle {
    base {
        Modifier
            .width(TouchTarget.value())
            .height(TouchTarget.value())
            .display(DisplayStyle.Grid)
            .borderRadius(RadiusControl.value())
            .border(1.px, LineStyle.Solid, Border.value())
            .backgroundColor(Surface.value())
            .color(OnSurfaceMuted.value())
            .cursor(Cursor.Pointer)
            .transitionSurfaceAction()
            .styleModifier {
                property("place-items", "center")
                property("padding", "0")
            }
    }
    focusVisible {
        Modifier
            .outline(0.px, LineStyle.None, Color("transparent"))
            .styleModifier { property("box-shadow", "0 0 0 3px ${AccentRing.value()}") }
    }
}

val AdminIconButtonNeutralStyle = AdminIconButtonBaseStyle.extendedBy {
    hover {
        Modifier
            .color(OnSurface.value())
            .border { color(BorderStrong.value()) }
            .translateY((-1).px)
    }
}

val AdminIconButtonOkStyle = AdminIconButtonBaseStyle.extendedBy {
    hover {
        Modifier
            .color(Success.value())
            .backgroundColor(SuccessSoft.value())
            .border { color(Success.value()) }
            .translateY((-1).px)
    }
}

val AdminIconButtonDangerStyle = AdminIconButtonBaseStyle.extendedBy {
    hover {
        Modifier
            .color(Danger.value())
            .backgroundColor(DangerSoft.value())
            .border { color(Danger.value()) }
            .translateY((-1).px)
    }
}

@Composable
fun AdminIconButton(
    icon: WebIcon,
    label: String,
    tone: AdminIconTone = AdminIconTone.Neutral,
    onClick: () -> Unit,
) {
    val style = when (tone) {
        AdminIconTone.Neutral -> AdminIconButtonNeutralStyle
        AdminIconTone.Ok -> AdminIconButtonOkStyle
        AdminIconTone.Danger -> AdminIconButtonDangerStyle
    }
    Button(
        attrs = style.toAttrs<AttrsScope<HTMLButtonElement>> {
            attr("aria-label", label)
            attr("title", label)
            onClick { event ->
                event.stopPropagation()
                onClick()
            }
        }
    ) {
        Icon(icon, size = 15)
    }
}

@Composable
fun AdminUserCell(name: String?, email: String? = null, avatarUrl: String? = null) {
    Div(
        attrs = Modifier
            .display(DisplayStyle.Flex)
            .alignItems(AlignItems.Center)
            .gap(11.px)
            .toAttrs()
    ) {
        AdminAvatar(name = name, url = avatarUrl, size = AdminAvatarSize.SM)
        Div(attrs = Modifier.styleModifier { property("min-width", "0") }.toAttrs()) {
            Div(
                attrs = Modifier
                    .fontSize(WebType.Body)
                    .fontWeight(600)
                    .color(OnSurface.value())
                    .toAttrs()
            ) { Text(name ?: "—") }
            if (email != null) {
                Div(
                    attrs = Modifier
                        .fontSize(WebType.Badge)
                        .color(OnSurfaceFaint.value())
                        .toAttrs()
                ) { Text(email) }
            }
        }
    }
}

@Composable
fun AdminNumCell(text: String, muted: Boolean = true) {
    Span(
        attrs = Modifier
            .fontWeight(700)
            .color((if (muted) OnSurfaceMuted else OnSurface).value())
            .styleModifier { property("font-variant-numeric", "tabular-nums") }
            .toAttrs()
    ) { Text(text) }
}

@Composable
fun AdminReasonCell(title: String, sub: String? = null) {
    Div {
        Div(
            attrs = Modifier
                .fontSize(WebType.Body)
                .fontWeight(600)
                .color(OnSurface.value())
                .toAttrs()
        ) { Text(title) }
        if (sub != null) {
            Span(
                attrs = Modifier
                    .display(DisplayStyle.Block)
                    .fontSize(WebType.Pill)
                    .color(OnSurfaceFaint.value())
                    .lineHeight(1.3)
                    .styleModifier { property("margin-top", "2px") }
                    .toAttrs()
            ) { Text(sub) }
        }
    }
}
